/**
 * How Patches read in the app: "Patched in with Sam · 4:12" while in one, "Patch going: Sam, Joey"
 * on a conversation, "Patch with Sam · 12 min" in call history. (The API words the notes and
 * notifications the same way: apps/api/src/patches/text.ts.)
 */

/** "Sam", "Sam and Joey", "Sam, Joey and Ann", "Sam, Joey and 2 others". */
export function namesText(names: readonly string[]): string {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  if (list.length === 3) return `${list[0]}, ${list[1]} and ${list[2]}`;
  return `${list[0]}, ${list[1]} and ${list.length - 2} others`;
}

/** A running clock: "0:07", "4:12", "1:02:03". */
export function clockText(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** The status while someone is in a Patch: "Patched in with Sam · 4:12" ("Patched in · 0:12" alone). */
export function statusText(others: readonly string[], seconds: number): string {
  const who = namesText(others);
  return `${who ? `Patched in with ${who}` : 'Patched in'} · ${clockText(seconds)}`;
}

/** On a conversation with a Patch going: "Patch going: Sam, Joey". */
export function goingText(names: readonly string[]): string {
  return `Patch going: ${names.filter(Boolean).join(', ')}`;
}

/** "under a minute", "12 min", "1 hr 5 min". */
export function durationText(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return 'under a minute';
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/**
 * What to say when sharing didn't start. Choosing nothing in the browser's picker (Cancel) says
 * nothing; anything else says what happened, with the browser's own reason for Stitch support.
 */
export function shareProblemText(err: unknown): string | null {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name ?? '';
  const message = e?.message ?? '';
  if (name === 'AbortError') return null;
  if (name === 'NotAllowedError') {
    // The computer itself said no (macOS: the browser isn't allowed to record the screen).
    if (/system/i.test(message)) {
      return 'Your computer isn’t letting this browser share your screen. On a Mac: System Settings → Privacy & Security → Screen & System Audio Recording, turn on your browser, then quit and reopen it.';
    }
    // Cancel in the picker.
    if (/denied/i.test(message) || !message) return null;
  }
  const why = [name, message].filter(Boolean).join(': ').slice(0, 160);
  return `Your screen couldn’t be shared. Try again, or choose a single window.${why ? ` (${why})` : ''}`;
}
